import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink, onAuthStateChanged, signInWithEmailLink } from 'firebase/auth';
import { auth } from '../../../firebase';
import { getOrgConfig } from '../../utils/orgResolution';
import { SERVICE_PRIVACY_NOTICE_URL } from '../../utils/familyPortal';
import { apiSendPasswordlessSignInLink } from '../../utils/rmsApi';
import useRmsAnalytics from '../../hooks/useRmsAnalytics';
import useEmailedSignInLink from '../../hooks/useEmailedSignInLink';
import HarbourBrandLockup from '../../components/harbour/HarbourBrandLockup';
import HarbourButton from '../../components/harbour/HarbourButton';
import { HarbourField } from '../../components/harbour/HarbourField';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import { SIGNED_OUT_NOTICE } from '../../constants/signedOutNotice';
import { SIGN_IN_LINK_LIFETIME } from '../../constants/signInLink';
import '../../components/harbour/harbour.css';
import './family.css';
import './familySignIn.css';

/*
 * FamilySignInPage (SEND & ND Access Portal, TDI-53 parent-or-carer side).
 *
 * Public passwordless (email-link) sign-in for parents and carers at
 * /rms/family/sign-in. The flow mirrors ServiceUserSignIn:
 *   - Enter an email, we send a sign-in link (backend generates and emails it)
 *     and remember the address locally for same-device completion.
 *   - Arriving on this page via the emailed link (our own one-time token,
 *     TDI-706) swaps the token for a session and goes on to the portal.
 *     Firebase email links sent before that complete with the stored email;
 *     on a different browser we ask for the email again first.
 *   - An invalid or expired link shows the "link expired" screen, whose action
 *     is simply to send a fresh link.
 *   - Anyone already signed in is sent straight to /rms/family/children.
 *
 * Signed out, so the page draws its own brand-only top bar rather than using
 * FamilyPortalShell (which assumes a signed-in user menu).
 */

const EMAIL_STORAGE_KEY = 'emailForSignIn';
const DEFAULT_CONTINUE = '/rms/family/children';

// Copy agreed in the TDI-53 designs (first version; implemented verbatim).
const COPY = {
  intro: 'Enter your email address and we will send you a link to sign in. You do not need a password.',
  emailLabel: 'Email address',
  sendLink: 'Send link',
  emailFormatError: 'Enter an email address in the correct format, like name@example.com',
  expiredTitle: 'This sign-in link has expired or has already been used',
  checkYourEmail: 'Check your email',
  sendAgain: 'Send the link again',
  useDifferentEmail: 'Use a different email address',
  needHelp: 'Need help? Contact the Special Educational Needs Coordinator (SENCO) at your child’s school.',
  // The same words as the link's own "Signing you in" step (VerifyAction),
  // so the hand-over from that step to this page reads as one screen.
  signingInTitle: 'Signing you in',
  signingInBody: 'This takes a few seconds. You do not need to tap the link again.',
  // A sign-in link opened on a device with no stored email (TDI-831 part 2),
  // word for word from the design (4331:181648 / 4331:181591).
  confirmTitle: 'Confirm your email address',
  confirmNoticeTitle: 'Check it’s you',
  confirmNoticeBody: 'This link was opened on a different phone, tablet or computer. To keep your child’s information safe, we need to check it’s you.',
  confirmEmailLabel: 'Type in the email address the link was sent to',
  confirmContinue: 'Continue',
  confirmError: 'We could not sign you in with that email address. Check it’s the address the link was sent to, then try again.',
};

// Same validation as ServiceUserSignIn: something@something.something.
function isValidEmail(emailStr) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);
}

// R-012: the time is the one place every sign-in surface agrees on.
function sentBody(email) {
  return `If ${email} is the email address your child’s school gave us, we have sent a sign-in link to it. The link works for ${SIGN_IN_LINK_LIFETIME}. Cannot find it? Check your spam folder.`;
}

export default function FamilySignInPage() {
  const { orgId } = useMemo(() => getOrgConfig(), []);
  const analytics = useRmsAnalytics(orgId);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState('');
  // view: 'signin' (default) | 'sent' (check your email) | 'expired' (link expired)
  const [view, setView] = useState('signin');
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [sendError, setSendError] = useState('');
  // Arrived via a sign-in link on a browser with no stored email: ask again.
  const [needsEmail, setNeedsEmail] = useState(false);

  const continuePath = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get('continue') || DEFAULT_CONTINUE;
  }, [location.search]);

  // Set by the sign-out handlers so the page can confirm the sign-out.
  const signedOut = useMemo(
    () => new URLSearchParams(location.search).get('signedout') === '1',
    [location.search],
  );

  // An invite or sign-in link that has already signed the parent in returns
  // here with their address (e=) and where to go next (continue): TDI-676.
  // Until Firebase says who is signed in, the page holds on "Signing you in"
  // rather than offering the form, then goes straight on. If nobody turns
  // out to be signed in, the form shows as usual.
  const arrivedSignedInFromLink = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.has('e') && params.has('continue');
  }, [location.search]);
  const [authResolved, setAuthResolved] = useState(false);

  // An emailed sign-in link with our own one-time token (TDI-706). While it
  // is being redeemed the page says "Signing you in"; a used, expired or
  // invalid link shows the expired-link screen, whose action sends a new one.
  const { link: emailedLink, status: emailedLinkStatus } = useEmailedSignInLink({
    defaultContinue: DEFAULT_CONTINUE,
    onError: (e) => analytics.error({ where: 'familySignIn.redeem', code: e?.code || 'unknown', message: String(e?.message || e) }),
  });
  useEffect(() => {
    if (emailedLinkStatus === 'failed') setView('expired');
  }, [emailedLinkStatus]);

  // Complete sign-in when the URL is an email sign-in link. The link is
  // single-use, so the attempt runs once per page load whatever re-renders.
  const completionAttempted = useRef(false);
  const completeIfLink = useCallback(async () => {
    const href = window.location.href;
    if (completionAttempted.current) return;
    if (!isSignInWithEmailLink(auth, href)) return;
    completionAttempted.current = true;
    let stored = '';
    try { stored = window.localStorage.getItem(EMAIL_STORAGE_KEY) || ''; } catch (_) {}
    if (!stored) {
      setNeedsEmail(true);
      return;
    }
    setBusy(true);
    setSendError('');
    try {
      await signInWithEmailLink(auth, stored, href);
      try { window.localStorage.removeItem(EMAIL_STORAGE_KEY); } catch (_) {}
      navigate(continuePath());
    } catch (e) {
      setView('expired');
      analytics.error({ where: 'familySignIn.complete', code: e?.code || 'unknown', message: String(e?.message || e) });
    } finally {
      setBusy(false);
    }
  }, [analytics, continuePath, navigate]);

  useEffect(() => {
    completeIfLink();
  }, [completeIfLink]);

  // Already signed in (e.g. completed elsewhere): straight to the portal.
  // Not while an emailed link is in hand: it decides who signs in and where
  // they go.
  useEffect(() => {
    if (emailedLink) return;
    if (auth?.currentUser) {
      navigate(continuePath(), { replace: true });
    }
  }, [continuePath, emailedLink, navigate]);

  useEffect(() => {
    if (emailedLink) return undefined;
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate(continuePath(), { replace: true });
        return;
      }
      setAuthResolved(true);
    });
    return () => unsub();
  }, [continuePath, emailedLink, navigate]);

  const sendLink = useCallback(async (address) => {
    setBusy(true);
    setSendError('');
    try {
      await apiSendPasswordlessSignInLink({ orgId, email: address, continuePath: DEFAULT_CONTINUE });
      try { window.localStorage.setItem(EMAIL_STORAGE_KEY, address); } catch (_) {}
      setSentTo(address);
      setView('sent');
    } catch (e) {
      // Not in the designs: machinery failure copy carried over from ServiceUserSignIn.
      setSendError('Failed to send sign-in link. Please try again.');
      analytics.error({ where: 'familySignIn.sendLink', code: e?.code || 'unknown', message: String(e?.message || e) });
    } finally {
      setBusy(false);
    }
  }, [analytics, orgId]);

  // Submit on the sign-in (and link-expired) form: validate, then send.
  const handleSend = useCallback(async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!isValidEmail(email)) {
      setFieldError(COPY.emailFormatError);
      return;
    }
    setFieldError('');
    await sendLink(email);
  }, [busy, email, sendLink]);

  // Submit on the ask-for-email-again form (link opened in another browser).
  const handleCompleteWithEmail = useCallback(async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!isValidEmail(email)) {
      setFieldError(COPY.emailFormatError);
      return;
    }
    setFieldError('');
    setBusy(true);
    setSendError('');
    try {
      await signInWithEmailLink(auth, email, window.location.href);
      try { window.localStorage.removeItem(EMAIL_STORAGE_KEY); } catch (_) {}
      navigate(continuePath());
    } catch (e) {
      analytics.error({ where: 'familySignIn.completePrompt', code: e?.code || 'unknown', message: String(e?.message || e) });
      if (e?.code === 'auth/invalid-action-code' || e?.code === 'auth/expired-action-code') {
        setNeedsEmail(false);
        setView('expired');
      } else {
        setSendError(COPY.confirmError);
      }
    } finally {
      setBusy(false);
    }
  }, [analytics, busy, continuePath, email, navigate]);

  const handleResend = useCallback(async () => {
    if (busy || !sentTo) return;
    await sendLink(sentTo);
  }, [busy, sendLink, sentTo]);

  const handleUseDifferentEmail = useCallback(() => {
    setEmail('');
    setFieldError('');
    setSendError('');
    setView('signin');
  }, []);

  const holdingForSignIn = (emailedLinkStatus === 'redeeming' && view === 'signin')
    || (arrivedSignedInFromLink && !authResolved && view === 'signin' && !needsEmail);
  const showForm = view === 'signin' || view === 'expired';

  return (
    <div className="harbour harbour-page family-page family-signin">
      <header className="family-signin-top-bar">
        <HarbourBrandLockup />
      </header>
      <main className="family-signin-main">
        {view === 'expired' && (
          <HarbourNotice kind="warning" title={COPY.expiredTitle} className="family-signin-notice">
            {/* The designs repeat the intro inside the notice on mobile only. */}
            <span className="family-signin-notice-body">{COPY.intro}</span>
          </HarbourNotice>
        )}
        {needsEmail && view === 'signin' && (
          <HarbourNotice kind="info" title={COPY.confirmNoticeTitle} className="family-signin-notice">
            {COPY.confirmNoticeBody}
          </HarbourNotice>
        )}

        {holdingForSignIn ? (
          <div className="family-signin-card" role="status" aria-live="polite">
            <div className="family-signin-head">
              <h1 className="family-signin-title">{COPY.signingInTitle}</h1>
              <p className="family-signin-intro">{COPY.signingInBody}</p>
            </div>
          </div>
        ) : showForm ? (
          <div className="family-signin-card">
            <div className="family-signin-head">
              <h1 className="family-signin-title">{needsEmail ? COPY.confirmTitle : 'Sign in'}</h1>
              {/* The confirm step has no intro: the notice above explains it. */}
              {!needsEmail && (
                <p className={`family-signin-intro ${view === 'expired' ? 'family-signin-intro--desktop-only' : ''}`.trim()}>
                  {COPY.intro}
                </p>
              )}
            </div>
            {signedOut && view === 'signin' && !needsEmail && (
              <HarbourNotice kind="success" title={SIGNED_OUT_NOTICE} />
            )}
            {!!sendError && (
              <HarbourNotice kind="error" className="family-signin-send-error">{sendError}</HarbourNotice>
            )}
            <form
              className="family-signin-form"
              onSubmit={needsEmail ? handleCompleteWithEmail : handleSend}
              noValidate
            >
              <HarbourField
                className="family-signin-field"
                label={needsEmail ? COPY.confirmEmailLabel : COPY.emailLabel}
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={fieldError}
                disabled={busy}
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="send"
              />
              <div className="family-signin-actions">
                <HarbourButton kind="family" type="submit" busy={busy} aria-busy={busy || undefined}>
                  {needsEmail ? COPY.confirmContinue : COPY.sendLink}
                </HarbourButton>
              </div>
            </form>
          </div>
        ) : (
          <div className="family-signin-card">
            <div className="family-signin-head">
              <h1 className="family-signin-title">{COPY.checkYourEmail}</h1>
            </div>
            <p className="family-signin-sent-body">{sentBody(sentTo)}</p>
            {!!sendError && (
              <HarbourNotice kind="error" className="family-signin-send-error">{sendError}</HarbourNotice>
            )}
            <div className="family-signin-recovery">
              <HarbourButton kind="secondary" onClick={handleResend} busy={busy} aria-busy={busy || undefined}>
                {COPY.sendAgain}
              </HarbourButton>
              <HarbourTextLink onClick={handleUseDifferentEmail} className="family-signin-alt-link">
                {COPY.useDifferentEmail}
              </HarbourTextLink>
            </div>
          </div>
        )}

        {/* Mobile: quiet help line under the content. Desktop: the same copy
            moves into the info notice in the side column (CSS toggles which
            one is displayed, so it is never shown twice at once). */}
        <p className="family-signin-help">{COPY.needHelp}</p>

        <nav className="family-signin-footer-links" aria-label="Portal links">
          {/* Help, Accessibility and Feedback have no destinations yet in
              TDI-53; the row is in every sign-in design, so they ship with
              placeholder targets. */}
          <a href="#help">Help</a>
          <span aria-hidden="true"> · </span>
          <a href={SERVICE_PRIVACY_NOTICE_URL} target="_blank" rel="noopener noreferrer">
            Privacy<span className="sr-only"> (opens in a new tab)</span>
          </a>
          <span aria-hidden="true"> · </span>
          <a href="#accessibility">Accessibility</a>
          <span aria-hidden="true"> · </span>
          <a href="#feedback">Feedback</a>
        </nav>

        <aside className="family-signin-aside">
          <HarbourNotice kind="info">{COPY.needHelp}</HarbourNotice>
        </aside>
      </main>
    </div>
  );
}
